import type { CalendarWorkOrder } from "@/shared/repositories/WorkOrderRepository";
import { Box, Button, Chip, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import type { DropLineInfo, PreviewDates } from "../hooks/useCalendarDrag";
import {
	buildWeekSegments,
	type DayKind,
	dayKeysBetween,
	type ShiftBand,
	formatTimeRange,
	getMonthWeeks,
	isDayInCurrentMonth,
	isPlainLeft,
	isToday,
	organizeSegmentsInRows,
	toDateKey,
	WEEKDAY_LABELS,
	type WeekSegment,
} from "../utils/calendarUtils";
import { DragModeSnackbar } from "./DragModeSnackbar";
import { DragMonthZones } from "./DragMonthZones";
import {
	type CalendarItem,
	DAY_CELL_PADDING,
	EVENT_ROW_HEIGHT,
	EventBar,
	itemColors,
	itemLines,
} from "./EventBar";

const EVENTS_LIMIT = 3;
const DAY_HEADER_HEIGHT = 28;
const DAY_CELL_MIN_HEIGHT = 118;

type Props = {
	currentDate: Date;
	/** date key -> scheme shape, only for days differing from the line default */
	scheduleByDay: Map<string, DayKind>;
	/** date key -> that calendar day's shift slices (used on drag preview) */
	shiftBandsByDay: Map<string, ShiftBand[]>;
	items: CalendarItem[];
	drag: { item: CalendarItem } | null;
	dropLine: DropLineInfo | null;
	preview: PreviewDates | null;
	freeMode: boolean;
	/** the grid still shows the previous month's plan (month flip mid-drag) */
	stale: boolean;
	ghostRef: React.RefObject<HTMLDivElement | null>;
	onMonthOffset: (offset: number) => void;
	onDayClick: (date: Date, anchorEl: HTMLElement) => void;
	onDayContextMenu: (e: React.MouseEvent, date: Date) => void;
	onSchemeChipClick: (date: Date) => void;
	onItemMouseDown: (e: React.MouseEvent, item: CalendarItem) => void;
	onItemContextMenu: (e: React.MouseEvent, item: CalendarItem) => void;
	onWorkOrderClick: (wo: CalendarWorkOrder) => void;
	menuTargetKey: string | null;
};

const DropCircle = ({
	x,
	y,
	color,
}: {
	x: number;
	y: number;
	color: string;
}) => (
	<Box
		sx={{
			position: "fixed",
			left: `${x - 6}px`,
			top: `${y}px`,
			width: 14,
			height: 14,
			borderRadius: "50%",
			backgroundColor: color,
			border: "2px solid white",
			zIndex: 10000,
			pointerEvents: "none",
			boxShadow: "0 2px 6px #00000040",
			transition: "all 0.1s ease",
		}}
	/>
);

const ShiftBandsOverlay = ({
	bands,
	previewStartMs,
}: {
	bands: ShiftBand[];
	previewStartMs: number | null;
}) => (
	<Box
		sx={{
			position: "absolute",
			inset: 0,
			pointerEvents: "none",
			zIndex: 0,
		}}
	>
		{bands.map((b) => {
			const landing =
				previewStartMs != null &&
				previewStartMs >= b.startMs &&
				previewStartMs < b.endMs;
			return (
				<Box
					key={b.startMs}
					sx={{
						position: "absolute",
						top: 0,
						bottom: 0,
						left: `${b.leftPct}%`,
						width: `${b.widthPct}%`,
						backgroundColor: landing ? "#FFC10773" : "#0000000D",
						borderLeft: "1px dashed #00000033",
					}}
				>
					<Box
						sx={{
							position: "absolute",
							bottom: 1,
							left: 0,
							right: 0,
							textAlign: "center",
							fontSize: 9,
							fontWeight: landing ? 800 : 600,
							opacity: landing ? 0.95 : 0.45,
						}}
					>
						{b.label}
					</Box>
				</Box>
			);
		})}
	</Box>
);

export const MonthGrid = ({
	currentDate,
	scheduleByDay,
	shiftBandsByDay,
	items,
	drag,
	dropLine,
	preview,
	freeMode,
	stale,
	ghostRef,
	onMonthOffset,
	onDayClick,
	onDayContextMenu,
	onSchemeChipClick,
	onItemMouseDown,
	onItemContextMenu,
	onWorkOrderClick,
	menuTargetKey,
}: Props) => {
	const weeks = useMemo(() => getMonthWeeks(currentDate), [currentDate]);
	const [expandedDays, setExpandedDays] = useState<Set<string>>(new Set());
	// Grid-level so a work order spanning weeks lights up on every row
	const [hoveredKey, setHoveredKey] = useState<string | null>(null);

	// Per week: segments stacked into rows; per day: the segments starting there
	const weekDays = useMemo(() => {
		const segments = buildWeekSegments(items, weeks);
		const byWeek: WeekSegment<CalendarItem>[][] = weeks.map(() => []);
		for (const seg of segments) byWeek[seg.weekIndex].push(seg);

		return byWeek.map((weekSegments) => {
			const rows = organizeSegmentsInRows(weekSegments);
			const perDay: {
				seg: WeekSegment<CalendarItem>;
				rowIndex: number;
			}[][] = Array.from({ length: 7 }, () => []);
			rows.forEach((row, rowIndex) =>
				row.forEach((seg) =>
					perDay[seg.firstDayInWeek].push({ seg, rowIndex }),
				),
			);
			return perDay;
		});
	}, [items, weeks]);

	const toggleDayExpansion = (dateKey: string) =>
		setExpandedDays((prev) => {
			const next = new Set(prev);
			if (next.has(dateKey)) next.delete(dateKey);
			else next.add(dateKey);
			return next;
		});

	const dropColor = dropLine?.position === "empty" ? "#1976d2" : "#0288d1";
	const ghostColors = drag ? itemColors(drag.item) : null;
	// Same card language as the confirm modal that follows the drop
	const [ghostTitle, ghostLine2] = drag ? itemLines(drag.item) : ["", ""];
	const ghostMeta =
		drag?.item.kind === "wo"
			? `WO # ${drag.item.wo.WorkOrderNumber ?? "—"} · ×${drag.item.wo.Quantity}`
			: ghostLine2;

	// Cells the dragged item would occupy at the previewed position
	const previewKeys = useMemo(
		() =>
			drag && preview
				? new Set(dayKeysBetween(preview.start, preview.end))
				: null,
		[drag, preview],
	);

	return (
		<Box sx={{ position: "relative" }}>
			{drag && (
				<DragMonthZones
					currentDate={currentDate}
					onMonthOffset={onMonthOffset}
				/>
			)}
			<Box
				sx={{
					border: "1px solid",
					borderColor: "divider",
					borderRadius: 1,
					overflow: "hidden",
					// The whole grid is a drag surface: a stray text selection on a day
					// number or a bar label turns a drag into a botched selection
					userSelect: "none",
					WebkitUserSelect: "none",
				}}
			>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "repeat(7, 1fr)",
						backgroundColor: "grey.100",
						borderBottom: "1px solid",
						borderColor: "divider",
					}}
				>
					{WEEKDAY_LABELS.map((label) => (
						<Typography
							key={label}
							variant="caption"
							sx={{ p: 0.75, textAlign: "center", fontWeight: 600 }}
						>
							{label}
						</Typography>
					))}
				</Box>

				{weeks.map((week, weekIndex) => (
					<Box
						key={toDateKey(week[0])}
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(7, 1fr)",
							borderBottom: "1px solid",
							borderColor: "divider",
							"&:last-child": { borderBottom: "none" },
						}}
					>
						{week.map((day, dayIndex) => {
							const dateKey = toDateKey(day);
							const inMonth = isDayInCurrentMonth(day, currentDate);
							const dayKind = scheduleByDay.get(dateKey) ?? null;

							const inPreview = !!previewKeys?.has(dateKey);
							const entries = weekDays[weekIndex][dayIndex];
							const isExpanded = expandedDays.has(dateKey);
							const maxRows = entries.length
								? Math.max(...entries.map((e) => e.rowIndex)) + 1
								: 0;
							const visibleEntries = isExpanded
								? entries
								: entries.filter((e) => e.rowIndex < EVENTS_LIMIT);
							const hiddenCount = entries.length - visibleEntries.length;
							const rowsShown = isExpanded
								? maxRows
								: Math.min(maxRows, EVENTS_LIMIT);

							return (
								<Box
									key={dateKey}
									data-date={dateKey}
									onClick={(e) =>
										inMonth &&
										isPlainLeft(e) &&
										onDayClick(day, e.currentTarget)
									}
									onContextMenu={(e) => inMonth && onDayContextMenu(e, day)}
									sx={{
										position: "relative",
										minHeight: DAY_CELL_MIN_HEIGHT,
										p: `${DAY_CELL_PADDING}px`,
										borderRight: "1px solid",
										borderColor: "divider",
										"&:last-child": { borderRight: "none" },
										cursor: inMonth ? "pointer" : "default",
										backgroundColor: inPreview
											? "#fff3cd"
											: inMonth
												? "background.paper"
												: "grey.50",
										borderTop: inPreview ? "3px solid #ffc107" : "none",
										transition:
											"background-color 0.15s ease, border-top 0.15s ease",
										"&:hover":
											inMonth && !inPreview
												? { backgroundColor: "action.hover" }
												: undefined,
									}}
								>
									{inPreview && (
										<ShiftBandsOverlay
											bands={shiftBandsByDay.get(dateKey) ?? []}
											previewStartMs={preview?.start.getTime() ?? null}
										/>
									)}
									<Box
										sx={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "center",
											height: DAY_HEADER_HEIGHT - 4,
											mb: "4px",
											position: "relative",
											zIndex: 1,
										}}
									>
										<Typography
											variant="body2"
											sx={{
												fontWeight: isToday(day) ? 700 : 400,
												color: inMonth ? "text.primary" : "text.disabled",
												...(isToday(day) && {
													backgroundColor: "primary.main",
													color: "primary.contrastText",
													borderRadius: "50%",
													width: 24,
													height: 24,
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
												}),
											}}
										>
											{day.getDate()}
										</Typography>

										{inMonth && dayKind && (
											<Chip
												label={dayKind}
												size="small"
												color={dayKind === "12h" ? "warning" : "info"}
												onMouseDown={(e) => e.stopPropagation()}
												onClick={(e) => {
													e.stopPropagation();
													onSchemeChipClick(day);
												}}
												sx={{
													height: 22,
													fontSize: 11,
													fontWeight: 700,
													cursor: "pointer",
												}}
											/>
										)}
									</Box>

									<Box
										sx={{
											position: "relative",
											minHeight:
												rowsShown * EVENT_ROW_HEIGHT +
												(hiddenCount > 0 ? 22 : 0),
										}}
									>
										{visibleEntries.map(({ seg, rowIndex }) => (
											<EventBar
												key={seg.item.key}
												seg={seg}
												rowIndex={rowIndex}
												isDragging={!!drag}
												draggedKey={drag?.item.key ?? null}
												isHovered={hoveredKey === seg.item.key}
												onHover={setHoveredKey}
												onItemMouseDown={onItemMouseDown}
												onItemContextMenu={onItemContextMenu}
												onWorkOrderClick={onWorkOrderClick}
												isMenuTarget={menuTargetKey === seg.item.key}
											/>
										))}

										{hiddenCount > 0 && (
											<Button
												size="small"
												onMouseDown={(e) => e.stopPropagation()}
												onClick={(e) => {
													e.stopPropagation();
													toggleDayExpansion(dateKey);
												}}
												sx={{
													position: "absolute",
													top: rowsShown * EVENT_ROW_HEIGHT,
													left: 0,
													fontSize: "0.7rem",
													textTransform: "none",
													padding: "2px 6px",
													minWidth: "auto",
													zIndex: 1,
												}}
											>
												+{hiddenCount} more
											</Button>
										)}
									</Box>
								</Box>
							);
						})}
					</Box>
				))}

				{drag && ghostColors && (
					<Box
						ref={ghostRef}
						sx={{
							position: "fixed",
							left: -9999,
							top: -9999,
							// grows with the part number instead of cutting it
							width: "max-content",
							minWidth: 300,
							maxWidth: 560,
							zIndex: 10001,
							pointerEvents: "none",
							backgroundColor: "#fff",
							color: "#333",
							border: "1px solid #ddd",
							borderLeft: `4px solid ${ghostColors.color}`,
							borderRadius: "4px",
							boxShadow: "0 6px 20px #00000059",
							whiteSpace: "nowrap",
							cursor: "grabbing",
						}}
					>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								px: 1.5,
								py: 0.75,
								borderBottom: "1px solid #e6e6e6",
							}}
						>
							<Box
								sx={{
									fontSize: "0.875rem",
									fontWeight: 700,
									minWidth: 0,
									overflow: "hidden",
									textOverflow: "ellipsis",
								}}
							>
								{ghostTitle}
							</Box>
							<Box
								sx={{
									ml: "auto",
									pl: 2,
									flexShrink: 0,
									fontSize: "0.75rem",
									color: "#666",
									fontVariantNumeric: "tabular-nums",
								}}
							>
								{ghostMeta}
							</Box>
						</Box>
						<Box
							sx={{
								display: "grid",
								gridTemplateColumns: "auto 1fr",
								columnGap: 1.5,
								rowGap: "1px",
								px: 1.5,
								pt: 0.75,
								pb: "7px",
								fontSize: "0.75rem",
								lineHeight: "17px",
								fontVariantNumeric: "tabular-nums",
							}}
						>
							<Box sx={{ color: "#666" }}>Old</Box>
							<Box
								sx={{
									textAlign: "right",
									color: "#999",
									textDecoration: "line-through",
								}}
							>
								{formatTimeRange(drag.item.start, drag.item.end)}
							</Box>
							{preview && (
								<>
									<Box sx={{ color: "#666" }}>New</Box>
									<Box sx={{ textAlign: "right", fontWeight: 700 }}>
										{formatTimeRange(preview.start, preview.end)}
									</Box>
								</>
							)}
							{stale && (
								<>
									<Box sx={{ color: "#666" }}>New</Box>
									<Box sx={{ textAlign: "right", color: "#999" }}>
										Loading month…
									</Box>
								</>
							)}
						</Box>
					</Box>
				)}

				{drag && dropLine && (
					<>
						<Box
							sx={{
								position: "fixed",
								left: `${dropLine.x}px`,
								top: `${dropLine.y + 12}px`,
								width: "3px",
								height: `${dropLine.height - 12}px`,
								backgroundColor: dropColor,
								zIndex: 9999,
								pointerEvents: "none",
								boxShadow: "0 0 4px #0000004C",
								transition: "all 0.1s ease",
							}}
						/>
						<DropCircle x={dropLine.x} y={dropLine.y} color={dropColor} />
						<DropCircle
							x={dropLine.x}
							y={dropLine.y + dropLine.height - 14}
							color={dropColor}
						/>
					</>
				)}

				<DragModeSnackbar visible={!!drag} freeMode={freeMode} />
			</Box>
		</Box>
	);
};
